// The graph of notes, sketches and what they link to, as in Obsidian's graph view.
import { Background, BackgroundVariant, Controls, Handle, Position, ReactFlow, ReactFlowProvider, useReactFlow, type Edge, type Node, type NodeProps } from '@xyflow/react';
import { Loader2 } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { stressLayout, type Positions } from '../diagram/layout';
import { cn } from '../lib/cn';
import { useWorkspace } from '../state/workspace';
import { KindIcon } from './LinkPreview';
import { parseLinks, resolveLink, targetKey, targetLabel, type LinkTarget } from '@modelviewer/core';
import { openTarget } from './navigate';
import { useLinkIndex } from './useLinkIndex';

interface GraphNodeData {
  [key: string]: unknown;
  label: string;
  target: LinkTarget;
  degree: number;
}

type GraphNodeType = Node<GraphNodeData, 'item'>;

const KIND_CLASS: Record<LinkTarget['kind'], string> = {
  note: 'border-accent/60 bg-accent-soft text-text',
  sketch: 'border-added/50 bg-added-soft text-text',
  entity: 'border-border-strong bg-panel-2 text-text',
  migration: 'border-border bg-panel text-muted',
  context: 'border-changed/50 bg-changed-soft text-text',
};

const ItemNode = memo(function ItemNode({ data }: NodeProps<GraphNodeType>) {
  return (
    <div className={cn('flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] whitespace-nowrap shadow-sm', KIND_CLASS[data.target.kind])} title={data.label}>
      <span className="text-faint">
        <KindIcon kind={data.target.kind} size={12} />
      </span>
      {data.label}
      {/* Both ends in the middle, so links run center to center, behind the pills. */}
      <Handle type="source" position={Position.Top} className="!opacity-0" style={{ top: '50%', left: '50%' }} isConnectable={false} />
      <Handle type="target" position={Position.Top} className="!opacity-0" style={{ top: '50%', left: '50%' }} isConnectable={false} />
    </div>
  );
});

const nodeTypes = { item: ItemNode };

export function GraphView() {
  return (
    <ReactFlowProvider>
      <Graph />
    </ReactFlowProvider>
  );
}

function Graph() {
  const notes = useWorkspace((s) => s.notes);
  const sketches = useWorkspace((s) => s.sketches);
  const index = useLinkIndex();
  const flow = useReactFlow();
  const [positions, setPositions] = useState<Positions | null>(null);

  const { nodes, edges } = useMemo(() => {
    const items = new Map<string, { target: LinkTarget; degree: number }>();
    const links: { from: string; to: string }[] = [];
    const add = (t: LinkTarget) => {
      const key = targetKey(t);
      if (!items.has(key)) items.set(key, { target: t, degree: 0 });
      return key;
    };
    for (const n of Object.values(notes)) add({ kind: 'note', id: n.id, title: n.title });
    for (const s of Object.values(sketches)) {
      const from = add({ kind: 'sketch', id: s.id, title: s.doc.title });
      const base = s.base;
      if (base?.sketchId && sketches[base.sketchId]) links.push({ from, to: add({ kind: 'sketch', id: base.sketchId, title: sketches[base.sketchId]!.doc.title }) });
      else if (base) {
        const context = index.models.find((m) => m.kind === 'context' && m.ref.setKey === base.setKey);
        if (context) links.push({ from, to: add({ kind: 'context', setKey: base.setKey, name: context.name }) });
      }
    }
    for (const n of Object.values(notes)) {
      for (const l of parseLinks(n.body)) {
        const t = resolveLink(l.target, index);
        // Links to a property count as links to its entity.
        if (t) links.push({ from: `note:${n.id}`, to: add(t.kind === 'entity' ? { ...t, property: null } : t) });
      }
    }
    const unique = new Map(links.filter((l) => l.from !== l.to).map((l) => [`${l.from}->${l.to}`, l]));
    for (const l of unique.values()) {
      items.get(l.from)!.degree++;
      items.get(l.to)!.degree++;
    }
    const nodes = [...items].map(([id, { target, degree }]) => ({ id, label: targetLabel(target), target, degree }));
    const edges = [...unique.values()].map((l) => ({ id: `${l.from}->${l.to}`, source: l.from, target: l.to }));
    return { nodes, edges };
  }, [notes, sketches, index]);

  const signature = useMemo(() => `${nodes.map((n) => n.id).join('|')}#${edges.map((e) => e.id).join('|')}`, [nodes, edges]);
  useEffect(() => {
    let cancelled = false;
    void stressLayout(
      nodes.map((n) => ({ id: n.id, width: 44 + n.label.length * 7, height: 32 })),
      edges.map((e) => ({ id: e.id, from: e.source, to: e.target })),
    ).then((p) => {
      if (cancelled) return;
      setPositions(p);
      requestAnimationFrame(() => void flow.fitView({ padding: 0.2, duration: 250, maxZoom: 1.2 }));
    });
    return () => {
      cancelled = true;
    };
    // `signature` stands for the nodes and edges, which are new objects on every change.
  }, [signature]);

  if (nodes.length === 0) {
    return <div className="flex h-full items-center justify-center p-6 text-center text-muted">No notes or sketches yet. Write a note and link to entities with [[Name]] to see them here.</div>;
  }
  if (!positions) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" /> Arranging…
      </div>
    );
  }
  const flowNodes: GraphNodeType[] = nodes.map((n) => ({ id: n.id, type: 'item', position: positions[n.id] ?? { x: 0, y: 0 }, data: { label: n.label, target: n.target, degree: n.degree } }));
  const flowEdges: Edge[] = edges.map((e) => ({ ...e, style: { stroke: 'var(--edge)', strokeWidth: 1.2 }, type: 'straight' }));
  return (
    <div className="h-full w-full" data-testid="graph">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => openTarget(node.data.target)}
        nodesConnectable={false}
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-center"
        deleteKeyCode={null}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.3} color="var(--canvas-dot)" />
        <Controls showInteractive={false} position="bottom-left" />
      </ReactFlow>
    </div>
  );
}
